import { Plus, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  EarlySettlementRule,
  InterestMethod,
  InterestRatePeriod,
  LoanFeeCalculation,
  LoanFeeCollectionMode,
  LoanProductDetail,
  LoanProductRequest,
  LoanProductStatus,
  MonthLengthConvention,
  PenaltyCalculation,
  RepaymentFrequency,
} from '@/api/loans-types'
import { MoneyInput } from '@/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BASE_CURRENCY, type Pesewas } from '@/lib/money'
import type { ApiError } from '@/lib/problem-details'

import {
  ALLOCATION_BUCKETS,
  EARLY_SETTLEMENT_LABELS,
  FEE_CALCULATION_LABELS,
  FEE_COLLECTION_LABELS,
  INTEREST_METHOD_LABELS,
  INTEREST_PERIOD_LABELS,
  MONTH_LENGTH_LABELS,
  PENALTY_CALCULATION_LABELS,
  REPAYMENT_FREQUENCY_LABELS,
  fractionToPercent,
  parsePercent,
  percentToFraction,
} from './constants'
import { FormField, NoteField, NumberField, OperationForm } from './operation-form'
import { Section } from './panel-primitives'

/**
 * Defining a loan product, which is defining every loan written against it.
 *
 * Three things this form is careful about, because each of them has been got wrong before.
 *
 * **A fee value is two different things.** `FlatAmount` is money and is keyed through
 * `MoneyInput` in pesewas; `PercentOfPrincipal` is a fraction and is keyed as a percentage. The
 * control changes with the calculation rather than staying a number box, because a number box is
 * how 5.00 becomes five pesewas. The same applies to the penalty rule.
 *
 * **Rates are fractions.** Everything the domain calls a percent — the interest rate, the
 * security deposit, the growth cap, the penalty cap — crosses the wire as a fraction in 0–1 and
 * is refused outside it. They are keyed here as percentages and converted once, at this boundary.
 *
 * **A product is priced as a set.** There is no partial update: create and amend send the whole
 * shape, so an amendment that forgot the fees would price a loan nobody configured.
 */

interface FeeDraft {
  key: string
  name: string
  calculation: LoanFeeCalculation
  /** Pesewas when the calculation is a flat amount. */
  amountMinor: Pesewas | null
  /** Percent as keyed — 2.5 means two and a half percent — when it is a percentage. */
  percent: string
  collection: LoanFeeCollectionMode
  feeIncomeAccountCode: string
}

interface PenaltyDraft {
  key: string
  calculation: PenaltyCalculation
  amountMinor: Pesewas | null
  percent: string
  capPercent: string
  penaltyIncomeAccountCode: string
}

const newKey = () => Math.random().toString(36).slice(2)

const emptyFee = (): FeeDraft => ({
  key: newKey(),
  name: '',
  calculation: 'FlatAmount',
  amountMinor: null,
  percent: '',
  collection: 'DeductFromDisbursement',
  feeIncomeAccountCode: '',
})

const emptyPenalty = (): PenaltyDraft => ({
  key: newKey(),
  calculation: 'FlatPerMissedInstalment',
  amountMinor: null,
  percent: '',
  capPercent: '',
  penaltyIncomeAccountCode: '',
})

function feeFromProduct(fee: LoanProductDetail['fees'][number]): FeeDraft {
  const isFlat = fee.calculation === 'FlatAmount'

  return {
    key: fee.id,
    name: fee.name,
    calculation: fee.calculation,
    amountMinor: isFlat ? fee.value : null,
    percent: isFlat ? '' : String(fractionToPercent(fee.value)),
    collection: fee.collection,
    feeIncomeAccountCode: fee.feeIncomeAccountCode,
  }
}

function penaltyFromProduct(rule: LoanProductDetail['penaltyRules'][number]): PenaltyDraft {
  const isFlat = rule.calculation === 'FlatPerMissedInstalment'

  return {
    key: rule.id,
    calculation: rule.calculation,
    amountMinor: isFlat ? rule.value : null,
    percent: isFlat ? '' : String(fractionToPercent(rule.value)),
    capPercent:
      rule.maxCapPercentOfPrincipal === null
        ? ''
        : String(fractionToPercent(rule.maxCapPercentOfPrincipal)),
    penaltyIncomeAccountCode: rule.penaltyIncomeAccountCode,
  }
}

/** A rate, keyed as a percentage. Never money — money goes through `MoneyInput`. */
function PercentField({
  id,
  label,
  hint,
  value,
  onChange,
  required = true,
}: {
  id: string
  label: string
  hint?: ReactNode
  value: string
  onChange: (value: string) => void
  required?: boolean
}) {
  return (
    <FormField id={id} label={label} hint={hint} required={required}>
      <div className="flex h-9 w-full items-center rounded-control border border-border-subtle bg-surface transition-colors focus-within:border-brand-text hover:border-border-strong">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step="0.01"
          min={0}
          max={100}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="numeric h-full w-full bg-transparent px-3 text-right text-sm text-heading tabular-nums outline-none"
        />
        <span className="pointer-events-none pr-3 text-xs font-medium text-muted select-none" aria-hidden>
          %
        </span>
      </div>
    </FormField>
  )
}

export interface LoanProductFormProps {
  /** The product being amended, or undefined when one is being defined. */
  product?: LoanProductDetail
  submitLabel: string
  notice?: ReactNode
  outcome?: ReactNode
  isPending?: boolean
  error?: ApiError | null
  onSubmit: (request: LoanProductRequest) => void
  onDone?: () => void
}

export function LoanProductForm({
  product,
  submitLabel,
  notice,
  outcome,
  isPending = false,
  error = null,
  onSubmit,
  onDone,
}: LoanProductFormProps) {
  const [code, setCode] = useState(product?.code ?? '')
  const [name, setName] = useState(product?.name ?? '')
  const [status, setStatus] = useState<LoanProductStatus>(product?.status ?? 'Active')
  const [interestMethod, setInterestMethod] = useState<InterestMethod>(
    product?.interestMethod ?? 'Flat',
  )
  const [interestRate, setInterestRate] = useState(
    product ? String(fractionToPercent(product.interestRate)) : '',
  )
  const [interestRatePeriod, setInterestRatePeriod] = useState<InterestRatePeriod>(
    product?.interestRatePeriod ?? 'PerMonth',
  )
  const [monthLength, setMonthLength] = useState<MonthLengthConvention>(
    product?.monthLengthConvention ?? 'FourWeekMonth',
  )
  const [frequency, setFrequency] = useState<RepaymentFrequency>(
    product?.repaymentFrequency ?? 'Weekly',
  )
  const [minInstalments, setMinInstalments] = useState(String(product?.minInstalments ?? ''))
  const [maxInstalments, setMaxInstalments] = useState(String(product?.maxInstalments ?? ''))
  const [minPrincipal, setMinPrincipal] = useState<Pesewas | null>(
    product?.minPrincipalMinor ?? null,
  )
  const [maxPrincipal, setMaxPrincipal] = useState<Pesewas | null>(
    product?.maxPrincipalMinor ?? null,
  )
  const [graceInstalments, setGraceInstalments] = useState(String(product?.graceInstalments ?? '0'))
  const [graceDays, setGraceDays] = useState(String(product?.graceDaysBeforePenalty ?? '0'))
  const [depositPercent, setDepositPercent] = useState(
    product ? String(fractionToPercent(product.securityDepositPercent)) : '',
  )
  const [compulsorySavings, setCompulsorySavings] = useState<Pesewas | null>(
    product?.compulsorySavingsPerInstalmentMinor ?? 0,
  )
  const [allocationOrder, setAllocationOrder] = useState<string[]>(
    product?.allocationOrder ?? [...ALLOCATION_BUCKETS],
  )
  const [allowEarlySettlement, setAllowEarlySettlement] = useState(
    product?.allowEarlySettlement ?? false,
  )
  const [earlySettlementRule, setEarlySettlementRule] = useState<EarlySettlementRule | null>(
    product?.earlySettlementRule ?? null,
  )
  const [requiresGroup, setRequiresGroup] = useState(product?.requiresGroup ?? false)
  const [growthPercent, setGrowthPercent] = useState(
    product ? String(fractionToPercent(product.maxRoundPrincipalGrowthPercent)) : '',
  )
  const [gl, setGl] = useState({
    principalAccountCode: product?.glMapping.principalAccountCode ?? '',
    interestIncomeAccountCode: product?.glMapping.interestIncomeAccountCode ?? '',
    penaltyIncomeAccountCode: product?.glMapping.penaltyIncomeAccountCode ?? '',
    feeIncomeAccountCode: product?.glMapping.feeIncomeAccountCode ?? '',
    writeOffExpenseAccountCode: product?.glMapping.writeOffExpenseAccountCode ?? '',
    allowanceAccountCode: product?.glMapping.allowanceAccountCode ?? '',
  })
  const [fees, setFees] = useState<FeeDraft[]>(product ? product.fees.map(feeFromProduct) : [])
  const [penalties, setPenalties] = useState<PenaltyDraft[]>(
    product ? product.penaltyRules.map(penaltyFromProduct) : [],
  )

  const numeric = (value: string) => Number.parseInt(value, 10)
  const rate = parsePercent(interestRate)
  const deposit = parsePercent(depositPercent)
  const growth = parsePercent(growthPercent)

  const glComplete = Object.values(gl).every((value) => value.trim() !== '')

  const feesComplete = fees.every(
    (fee) =>
      fee.name.trim() !== '' &&
      fee.feeIncomeAccountCode.trim() !== '' &&
      (fee.calculation === 'FlatAmount'
        ? fee.amountMinor !== null && fee.amountMinor > 0
        : Number.isFinite(parsePercent(fee.percent)) && parsePercent(fee.percent) > 0),
  )

  const penaltiesComplete = penalties.every(
    (rule) =>
      rule.penaltyIncomeAccountCode.trim() !== '' &&
      (rule.calculation === 'FlatPerMissedInstalment'
        ? rule.amountMinor !== null && rule.amountMinor > 0
        : Number.isFinite(parsePercent(rule.percent)) && parsePercent(rule.percent) > 0),
  )

  const valid =
    code.trim() !== '' &&
    name.trim() !== '' &&
    Number.isFinite(rate) &&
    rate >= 0 &&
    rate <= 100 &&
    Number.isInteger(numeric(minInstalments)) &&
    Number.isInteger(numeric(maxInstalments)) &&
    numeric(minInstalments) > 0 &&
    numeric(maxInstalments) >= numeric(minInstalments) &&
    minPrincipal !== null &&
    maxPrincipal !== null &&
    minPrincipal > 0 &&
    maxPrincipal >= minPrincipal &&
    Number.isInteger(numeric(graceInstalments)) &&
    numeric(graceInstalments) < numeric(minInstalments) &&
    Number.isInteger(numeric(graceDays)) &&
    Number.isFinite(deposit) &&
    deposit >= 0 &&
    deposit <= 100 &&
    compulsorySavings !== null &&
    Number.isFinite(growth) &&
    growth >= 0 &&
    allocationOrder.length === ALLOCATION_BUCKETS.length &&
    (!allowEarlySettlement || earlySettlementRule !== null) &&
    glComplete &&
    feesComplete &&
    penaltiesComplete

  const moveBucket = (index: number, delta: number) => {
    const next = [...allocationOrder]
    const target = index + delta
    if (target < 0 || target >= next.length) {
      return
    }
    const moved = next[index]
    next[index] = next[target]
    next[target] = moved
    setAllocationOrder(next)
  }

  const build = (): LoanProductRequest => ({
    code: code.trim(),
    name: name.trim(),
    currencyCode: product?.currencyCode ?? BASE_CURRENCY,
    status,
    interestMethod,
    interestRate: percentToFraction(rate),
    interestRatePeriod,
    monthLengthConvention: monthLength,
    repaymentFrequency: frequency,
    minInstalments: numeric(minInstalments),
    maxInstalments: numeric(maxInstalments),
    minPrincipalMinor: minPrincipal ?? 0,
    maxPrincipalMinor: maxPrincipal ?? 0,
    graceInstalments: numeric(graceInstalments),
    graceDaysBeforePenalty: numeric(graceDays),
    securityDepositPercent: percentToFraction(deposit),
    compulsorySavingsPerInstalmentMinor: compulsorySavings ?? 0,
    allocationOrder,
    allowEarlySettlement,
    // Null is what the server expects when settlement is not allowed, and it refuses the pair the
    // other way round. Sent explicitly rather than omitted: absent and null are different facts.
    earlySettlementRule: allowEarlySettlement ? earlySettlementRule : null,
    requiresGroup,
    maxRoundPrincipalGrowthPercent: percentToFraction(growth),
    glMapping: {
      principalAccountCode: gl.principalAccountCode.trim(),
      interestIncomeAccountCode: gl.interestIncomeAccountCode.trim(),
      penaltyIncomeAccountCode: gl.penaltyIncomeAccountCode.trim(),
      feeIncomeAccountCode: gl.feeIncomeAccountCode.trim(),
      writeOffExpenseAccountCode: gl.writeOffExpenseAccountCode.trim(),
      allowanceAccountCode: gl.allowanceAccountCode.trim(),
    },
    fees: fees.map((fee) => ({
      name: fee.name.trim(),
      calculation: fee.calculation,
      // Minor units for a flat fee, a fraction for a percentage. The server converts a flat fee
      // back the same way, so what is read is what was written.
      value:
        fee.calculation === 'FlatAmount'
          ? (fee.amountMinor ?? 0)
          : percentToFraction(parsePercent(fee.percent)),
      collection: fee.collection,
      feeIncomeAccountCode: fee.feeIncomeAccountCode.trim(),
    })),
    penaltyRules: penalties.map((rule) => ({
      trigger: 'LateInstalment',
      calculation: rule.calculation,
      value:
        rule.calculation === 'FlatPerMissedInstalment'
          ? (rule.amountMinor ?? 0)
          : percentToFraction(parsePercent(rule.percent)),
      maxCapPercentOfPrincipal:
        rule.capPercent.trim() === ''
          ? null
          : percentToFraction(parsePercent(rule.capPercent)),
      penaltyIncomeAccountCode: rule.penaltyIncomeAccountCode.trim(),
    })),
  })

  return (
    <OperationForm
      title={product ? `Amend ${product.name}` : 'Define a loan product'}
      description="A product is priced as a set: the whole definition is sent, on create and on amend alike."
      submitLabel={submitLabel}
      notice={notice}
      outcome={outcome}
      isPending={isPending}
      disabled={!valid}
      error={error}
      onDone={onDone}
      onSubmit={() => {
        if (valid) {
          onSubmit(build())
        }
      }}
      footnote="Amending a product never reaches a round whose terms are already frozen. It prices the next loan, not the one somebody has signed for."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <NoteField
          id="product-code"
          label="Code"
          required
          value={code}
          onChange={setCode}
          placeholder="VB-WEEKLY"
          hint="Unique across the institution. It appears on statements and in the bureau extract."
        />
        <NoteField
          id="product-name"
          label="Name"
          required
          value={name}
          onChange={setName}
          placeholder="Village banking, weekly"
        />

        <FormField id="product-status" label="Status" required>
          <Select value={status} onValueChange={(next) => setStatus(next as LoanProductStatus)}>
            <SelectTrigger id="product-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active — takes new applications</SelectItem>
              <SelectItem value="Retired">Retired — no new applications</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          id="product-currency"
          label="Currency"
          hint="GHS is the only transacting currency. The code travels with every amount anyway."
        >
          <Input id="product-currency" value={product?.currencyCode ?? BASE_CURRENCY} readOnly />
        </FormField>
      </div>

      <Section title="Pricing" description="Rates are fractions on the wire; they are keyed here as percentages.">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="product-method" label="Interest method" required>
            <Select
              value={interestMethod}
              onValueChange={(next) => setInterestMethod(next as InterestMethod)}
            >
              <SelectTrigger id="product-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(INTEREST_METHOD_LABELS) as InterestMethod[]).map((method) => (
                  <SelectItem key={method} value={method}>
                    {INTEREST_METHOD_LABELS[method]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <PercentField
            id="product-rate"
            label="Interest rate"
            value={interestRate}
            onChange={setInterestRate}
            hint="Between 0 and 100 percent. Sent as a fraction — 3.5% is 0.035."
          />

          <FormField id="product-period" label="Rate period" required>
            <Select
              value={interestRatePeriod}
              onValueChange={(next) => setInterestRatePeriod(next as InterestRatePeriod)}
            >
              <SelectTrigger id="product-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(INTEREST_PERIOD_LABELS) as InterestRatePeriod[]).map((period) => (
                  <SelectItem key={period} value={period}>
                    {INTEREST_PERIOD_LABELS[period]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id="product-month-length"
            label="Month length"
            required
            hint="A four-week month is the village-banking convention and is not a calendar month."
          >
            <Select
              value={monthLength}
              onValueChange={(next) => setMonthLength(next as MonthLengthConvention)}
            >
              <SelectTrigger id="product-month-length">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(MONTH_LENGTH_LABELS) as MonthLengthConvention[]).map((convention) => (
                  <SelectItem key={convention} value={convention}>
                    {MONTH_LENGTH_LABELS[convention]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField id="product-frequency" label="Repayment frequency" required>
            <Select
              value={frequency}
              onValueChange={(next) => setFrequency(next as RepaymentFrequency)}
            >
              <SelectTrigger id="product-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(REPAYMENT_FREQUENCY_LABELS) as RepaymentFrequency[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {REPAYMENT_FREQUENCY_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <PercentField
            id="product-growth"
            label="Growth cap on the next round"
            value={growthPercent}
            onChange={setGrowthPercent}
            hint="How much larger the next round may be than the last. 50% allows GHS 1,000 to become GHS 1,500."
          />
        </div>
      </Section>

      <Section title="Limits" description="Checked on every application, and again at approval.">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="product-min-principal" label="Smallest loan" required>
            <MoneyInput
              id="product-min-principal"
              value={minPrincipal}
              onChange={setMinPrincipal}
            />
          </FormField>
          <FormField id="product-max-principal" label="Largest loan" required>
            <MoneyInput
              id="product-max-principal"
              value={maxPrincipal}
              onChange={setMaxPrincipal}
            />
          </FormField>

          <NumberField
            id="product-min-instalments"
            label="Fewest instalments"
            value={minInstalments}
            onChange={setMinInstalments}
            min={1}
          />
          <NumberField
            id="product-max-instalments"
            label="Most instalments"
            value={maxInstalments}
            onChange={setMaxInstalments}
            min={1}
          />

          <NumberField
            id="product-grace-instalments"
            label="Grace instalments"
            value={graceInstalments}
            onChange={setGraceInstalments}
            min={0}
            hint="Instalments at the start carrying no principal. Must be fewer than the minimum instalment count."
          />
          <NumberField
            id="product-grace-days"
            label="Grace days before penalty"
            value={graceDays}
            onChange={setGraceDays}
            min={0}
            hint="Days past a due date before the ageing run charges anything."
          />

          <PercentField
            id="product-deposit"
            label="Security deposit"
            value={depositPercent}
            onChange={setDepositPercent}
            hint="As a percentage of principal. It is not withdrawable and is released by an offset or at closure."
          />

          <FormField
            id="product-savings"
            label="Compulsory savings per instalment"
            hint="Collected with each repayment. Not part of what the loan owes."
          >
            <MoneyInput
              id="product-savings"
              value={compulsorySavings}
              onChange={setCompulsorySavings}
            />
          </FormField>
        </div>

        <div className="mt-4 space-y-2">
          <Label>Allocation order</Label>
          <p className="text-xs text-muted">
            How a repayment is applied within an instalment. The oldest instalment is always
            settled first; this decides the order inside it.
          </p>
          <ol className="space-y-1">
            {allocationOrder.map((bucket, index) => (
              <li
                key={bucket}
                className="flex items-center justify-between gap-2 rounded-control border border-border-subtle px-3 py-1.5 text-sm"
              >
                <span>
                  <span className="numeric mr-2 text-xs text-muted tabular-nums">{index + 1}</span>
                  {bucket}
                </span>
                <span className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === 0}
                    onClick={() => moveBucket(index, -1)}
                    aria-label={`Move ${bucket} earlier`}
                  >
                    Up
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={index === allocationOrder.length - 1}
                    onClick={() => moveBucket(index, 1)}
                    aria-label={`Move ${bucket} later`}
                  >
                    Down
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-2">
            <Checkbox
              id="product-requires-group"
              checked={requiresGroup}
              onCheckedChange={(checked) => setRequiresGroup(checked === true)}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="product-requires-group" className="text-sm font-normal text-body">
                Lends only to members of a group
              </Label>
              <p className="text-xs text-muted">
                An account opened without a group is refused, and a further round is refused where
                the group&apos;s health does not qualify it.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="product-early-settlement"
              checked={allowEarlySettlement}
              onCheckedChange={(checked) => {
                const next = checked === true
                setAllowEarlySettlement(next)
                if (!next) {
                  setEarlySettlementRule(null)
                }
              }}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="product-early-settlement" className="text-sm font-normal text-body">
                Allows early settlement
              </Label>
              <p className="text-xs text-muted">
                A member may clear the round early at a quoted figure.
              </p>
            </div>
          </div>

          {allowEarlySettlement ? (
            <FormField
              id="product-settlement-rule"
              label="Early settlement rule"
              required
              hint="What the member pays for clearing early. Required once early settlement is allowed."
            >
              <Select
                value={earlySettlementRule ?? ''}
                onValueChange={(next) => setEarlySettlementRule(next as EarlySettlementRule)}
              >
                <SelectTrigger id="product-settlement-rule">
                  <SelectValue placeholder="Choose a rule" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(EARLY_SETTLEMENT_LABELS) as EarlySettlementRule[]).map((rule) => (
                    <SelectItem key={rule} value={rule}>
                      {EARLY_SETTLEMENT_LABELS[rule]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          ) : null}
        </div>
      </Section>

      <Section
        title="Ledger accounts"
        description="Where this product's money lands. Codes from the chart of accounts, not identifiers."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              ['principalAccountCode', 'Principal'],
              ['interestIncomeAccountCode', 'Interest income'],
              ['penaltyIncomeAccountCode', 'Penalty income'],
              ['feeIncomeAccountCode', 'Fee income'],
              ['writeOffExpenseAccountCode', 'Write-off expense'],
              ['allowanceAccountCode', 'Allowance for losses'],
            ] as const
          ).map(([key, label]) => (
            <NoteField
              key={key}
              id={`product-gl-${key}`}
              label={label}
              required
              value={gl[key]}
              onChange={(value) => setGl((current) => ({ ...current, [key]: value }))}
            />
          ))}
        </div>
      </Section>

      <Section
        title="Fees"
        description="A flat fee is money; a percentage fee is a fraction of principal. The control changes with the choice."
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setFees((current) => [...current, emptyFee()])}
          >
            <Plus />
            Add a fee
          </Button>
        }
      >
        {fees.length === 0 ? (
          <p className="text-xs text-muted">No fees. Loans on this product carry none.</p>
        ) : (
          <div className="space-y-4">
            {fees.map((fee, index) => (
              <div key={fee.key} className="space-y-3 rounded-control border border-border-subtle p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-heading">Fee {index + 1}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFees((current) => current.filter((row) => row.key !== fee.key))}
                    aria-label={`Remove fee ${index + 1}`}
                  >
                    <Trash2 />
                    Remove
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <NoteField
                    id={`fee-name-${fee.key}`}
                    label="Name"
                    required
                    value={fee.name}
                    onChange={(value) =>
                      setFees((current) =>
                        current.map((row) => (row.key === fee.key ? { ...row, name: value } : row)),
                      )
                    }
                    placeholder="Processing fee"
                  />

                  <FormField id={`fee-calc-${fee.key}`} label="Calculation" required>
                    <Select
                      value={fee.calculation}
                      onValueChange={(next) =>
                        setFees((current) =>
                          current.map((row) =>
                            row.key === fee.key
                              ? { ...row, calculation: next as LoanFeeCalculation }
                              : row,
                          ),
                        )
                      }
                    >
                      <SelectTrigger id={`fee-calc-${fee.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(FEE_CALCULATION_LABELS) as LoanFeeCalculation[]).map(
                          (calculation) => (
                            <SelectItem key={calculation} value={calculation}>
                              {FEE_CALCULATION_LABELS[calculation]}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </FormField>

                  {fee.calculation === 'FlatAmount' ? (
                    <FormField
                      id={`fee-amount-${fee.key}`}
                      label="Amount"
                      required
                      hint="A money amount, in cedis and pesewas."
                    >
                      <MoneyInput
                        id={`fee-amount-${fee.key}`}
                        value={fee.amountMinor}
                        onChange={(value) =>
                          setFees((current) =>
                            current.map((row) =>
                              row.key === fee.key ? { ...row, amountMinor: value } : row,
                            ),
                          )
                        }
                      />
                    </FormField>
                  ) : (
                    <PercentField
                      id={`fee-percent-${fee.key}`}
                      label="Percentage of principal"
                      value={fee.percent}
                      onChange={(value) =>
                        setFees((current) =>
                          current.map((row) =>
                            row.key === fee.key ? { ...row, percent: value } : row,
                          ),
                        )
                      }
                      hint="Above 0 and up to 100 percent."
                    />
                  )}

                  <FormField id={`fee-collection-${fee.key}`} label="Collection" required>
                    <Select
                      value={fee.collection}
                      onValueChange={(next) =>
                        setFees((current) =>
                          current.map((row) =>
                            row.key === fee.key
                              ? { ...row, collection: next as LoanFeeCollectionMode }
                              : row,
                          ),
                        )
                      }
                    >
                      <SelectTrigger id={`fee-collection-${fee.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(FEE_COLLECTION_LABELS) as LoanFeeCollectionMode[]).map(
                          (mode) => (
                            <SelectItem key={mode} value={mode}>
                              {FEE_COLLECTION_LABELS[mode]}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <NoteField
                    id={`fee-account-${fee.key}`}
                    label="Fee income account"
                    required
                    value={fee.feeIncomeAccountCode}
                    onChange={(value) =>
                      setFees((current) =>
                        current.map((row) =>
                          row.key === fee.key ? { ...row, feeIncomeAccountCode: value } : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Penalty"
        description="What a missed instalment costs. The ageing run is what charges it."
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setPenalties((current) => [...current, emptyPenalty()])}
          >
            <Plus />
            Add a rule
          </Button>
        }
      >
        {penalties.length === 0 ? (
          <p className="text-xs text-muted">
            No penalty rule. Late instalments on this product accrue nothing.
          </p>
        ) : (
          <div className="space-y-4">
            {penalties.map((rule, index) => (
              <div key={rule.key} className="space-y-3 rounded-control border border-border-subtle p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-heading">
                    Rule {index + 1} — on a late instalment
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setPenalties((current) => current.filter((row) => row.key !== rule.key))
                    }
                    aria-label={`Remove penalty rule ${index + 1}`}
                  >
                    <Trash2 />
                    Remove
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField id={`penalty-calc-${rule.key}`} label="Calculation" required>
                    <Select
                      value={rule.calculation}
                      onValueChange={(next) =>
                        setPenalties((current) =>
                          current.map((row) =>
                            row.key === rule.key
                              ? { ...row, calculation: next as PenaltyCalculation }
                              : row,
                          ),
                        )
                      }
                    >
                      <SelectTrigger id={`penalty-calc-${rule.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(PENALTY_CALCULATION_LABELS) as PenaltyCalculation[]).map(
                          (calculation) => (
                            <SelectItem key={calculation} value={calculation}>
                              {PENALTY_CALCULATION_LABELS[calculation]}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </FormField>

                  {rule.calculation === 'FlatPerMissedInstalment' ? (
                    <FormField
                      id={`penalty-amount-${rule.key}`}
                      label="Amount per missed instalment"
                      required
                      hint="A money amount, in cedis and pesewas."
                    >
                      <MoneyInput
                        id={`penalty-amount-${rule.key}`}
                        value={rule.amountMinor}
                        onChange={(value) =>
                          setPenalties((current) =>
                            current.map((row) =>
                              row.key === rule.key ? { ...row, amountMinor: value } : row,
                            ),
                          )
                        }
                      />
                    </FormField>
                  ) : (
                    <PercentField
                      id={`penalty-percent-${rule.key}`}
                      label="Percentage of the overdue amount, per week"
                      value={rule.percent}
                      onChange={(value) =>
                        setPenalties((current) =>
                          current.map((row) =>
                            row.key === rule.key ? { ...row, percent: value } : row,
                          ),
                        )
                      }
                    />
                  )}

                  <PercentField
                    id={`penalty-cap-${rule.key}`}
                    label="Cap, as a percentage of principal"
                    required={false}
                    value={rule.capPercent}
                    onChange={(value) =>
                      setPenalties((current) =>
                        current.map((row) =>
                          row.key === rule.key ? { ...row, capPercent: value } : row,
                        ),
                      )
                    }
                    hint="Leave empty for no ceiling. Empty is sent as null, which is what uncapped means."
                  />

                  <NoteField
                    id={`penalty-account-${rule.key}`}
                    label="Penalty income account"
                    required
                    value={rule.penaltyIncomeAccountCode}
                    onChange={(value) =>
                      setPenalties((current) =>
                        current.map((row) =>
                          row.key === rule.key
                            ? { ...row, penaltyIncomeAccountCode: value }
                            : row,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {!valid ? (
        <Alert tone="info">
          <AlertTitle>Not ready to send</AlertTitle>
          <AlertDescription>
            Every field above is required, the largest loan cannot be smaller than the smallest,
            grace instalments must be fewer than the minimum instalment count, and a product that
            allows early settlement needs a rule for it. Each fee and penalty rule needs its amount
            and its income account.
          </AlertDescription>
        </Alert>
      ) : null}
    </OperationForm>
  )
}
